import { getPlanConfigApi } from '@/api/plan/config';
import { getDeliveryContentApi } from '@/api/tool/delivery-content';
import { getSiteSetApi } from '@/api/tool/site-set';
import { getTargetingDictionaryApi } from '@/api/tool/targeting';
import { getAudienceListApi } from '@/api/tool/audience';
import { transformTreeKey } from '@/tools/form';
import { getDynamicComponentApi } from '@/api/creative';
import store from '@/store';
/**
 * 数据源管理控制器
 */
export default class DataSourceController {
  constructor(config) {
    this.config = config;
  }

  /**
   * 获取渠道接口枚举值
   * @returns {string}
   */
  getChannelInterfaceEnum() {
    const { channel_interface } = this.config.init_ops;
    return channel_interface;
  }

  /**
   * 获取请求的全部广告主账户id
   * @returns {string[]|number[]}
   */
  getRequestAdvertisers() {
    const { advertiser_ids } = this.config.init_ops;
    return advertiser_ids;
  }

  /**
   * 获取单个请求的广告主账户id
   * @returns {string|number}
   */
  getRequestAdvertiser() {
    const { advertiser_ids } = this.config.init_ops;
    return advertiser_ids[0];
  }

  /**
   * 获取渠道所属配置
   */
  getPlanConfig() {
    return new Promise((resolve, reject) => {
      getPlanConfigApi({
        channel_interface: this.getChannelInterfaceEnum(),
      }).then(resolve).catch(reject);
    });
  }

  /**
   * 获取投放平台枚举
   */
  async getDeliveryPlatformEnums() {
    try {
      const channel_interface = this.getChannelInterfaceEnum();
      const data = channel_interface ? store.getters[`${channel_interface}Enum`].api_fusion_delivery_platform_type : [];
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * 获取投放平台枚举值
   */
  async getDeliveryPlatformEnumsVal() {
    try {
      const channel_interface = this.getChannelInterfaceEnum();
      const data = channel_interface ? store.getters[`${channel_interface}EnumVal`].api_fusion_delivery_platform_type : {};
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * 获取投放内容
   * @param {Object} filtering 渠道过滤条件
   * @returns {Promise<Object[]>}
   */
  async getDeliveryContent(filtering = {}) {
    try {
      const channel_interface = this.getChannelInterfaceEnum();
      const advertiser_id = this.getRequestAdvertiser();
      const params = {
        channel_interface,
        advertiser_id,
        filtering,
      };
      const ret = await getDeliveryContentApi(params);
      const result = (ret?.list || []).map(({ id, name }) => ({ label: `${name}（${id}）`, value: `${id}#${name}` }));
      return Promise.resolve(result);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * 获取版位列表
   * @param {Object} filtering 渠道过滤条件
   * @returns {Promise<Object[]>}
   */
  async getSiteSet(filtering = {}) {
    try {
      const channel_interface = this.getChannelInterfaceEnum();
      const advertiser_id = this.getRequestAdvertiser();
      const params = {
        channel_interface,
        advertiser_id,
        ...filtering,
      };
      const ret = await getSiteSetApi(params);
      const list = transformTreeKey({
        options: ret.list,
        label_key: 'name',
        value_key: 'id',
      });
      /** 提示信息 */
      const tips_map = {
        'SITE_SET_WECHAT|SITE_SET_WECHAT_PLUGIN': '微信公众号与小程序版位包括微信公众号与小程序版位和微信新闻插件版位',
      };
      const result = list.map(item => {
        return Object.assign(item, {
          ...(tips_map[item.value] && {
            tips: tips_map[item.value],
          }),
        });
      });
      return Promise.resolve(result);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * 获取转化目标枚举值
   * @returns {Promise<Object>}
   */
  async getConvertGoalEnumVal() {
    try {
      const channel_interface = this.getChannelInterfaceEnum();
      const data = channel_interface ? store.getters[`${channel_interface}EnumVal`].api_fusion_convert_goal : {};
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * 获取转化目标枚举
   * @returns {Promise<Object>}
   */
  async getConvertGoalEnums() {
    try {
      const channel_interface = this.getChannelInterfaceEnum();
      const data = channel_interface ? store.getters[`${channel_interface}Enum`].api_fusion_convert_goal : {};
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * 获取出价方式枚举
   */
  async getPriceTypeEnums() {
    try {
      const channel_interface = this.getChannelInterfaceEnum();
      const data = channel_interface ? store.getters[`${channel_interface}Enum`].api_fusion_price_type : [];
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * 获取出价策略枚举
   */
  async getPriceStrategyEnums() {
    try {
      const channel_interface = this.getChannelInterfaceEnum();
      const data = channel_interface ? store.getters[`${channel_interface}Enum`].api_fusion_price_strategy : [];
      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * 获取定向字典
   */
  getTargetingDictionary(targeting_fields = ['geo_location', 'gender', 'age', 'device', 'device_price', 'network_type']) {
    return new Promise((resolve, reject) => {
      const channel_interface = this.getChannelInterfaceEnum();
      const advertiser_id = this.getRequestAdvertiser();
      const params = {
        channel_interface,
        advertiser_id,
        targeting_fields,
      };
      getTargetingDictionaryApi(params).then(resolve).catch(reject);
    });
  }

  /**
   * 获取人群包列表
   * @param {Object} filtering 过滤项
   */
  getAudienceList(filtering = {}) {
    return new Promise((resolve, reject) => {
      const channel_interface = this.getChannelInterfaceEnum();
      const advertiser_id = this.getRequestAdvertiser();
      const params = {
        channel_interface,
        advertiser_id,
        ...filtering,
      };
      getAudienceListApi(params).then(res => {
        const result = (res?.list || []).map(({ id, name, ...rest }) => {
          return {
            label: name,
            value: id,
            ...rest,
            disabled: false,
          };
        });
        resolve(result);
      }).catch(reject);
    });
  }

  /**
   * 获取动态渲染组件
   * @param {Object} params 查询参数
   * @returns {Object}
   */
  getDynamicComponent(params = {}) {
    return new Promise((resolve, reject) => {
      const channel_interface = this.getChannelInterfaceEnum();
      const advertiser_id = this.getRequestAdvertiser();
      const payload = {
        channel_interface,
        advertiser_id,
        ...params,
      };
      getDynamicComponentApi(payload).then(res => {
        resolve({
          list: res.list,
          names: res.list.map(({ name }) => name),
        });
      }).catch(reject);
    });
  }
}
